import { useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';

interface PlayerBuilding {
  id: string;
  buildingType: string;
  level: number;
  gridX: number;
  gridY: number;
}

interface GameCanvasProps {
  selectedUnit: string | null;
  buildings: PlayerBuilding[];
}

// ── Isometric Konstanten ─────────────────────────
const TILE_W = 64;  // Breite einer Iso-Kachel
const TILE_H = 32;  // Höhe einer Iso-Kachel
const BUILDING_H = 24; // Höhe des Gebäudes (für 3D-Effekt)

// Iso-Koordinaten: Grid (gx, gy) → Screen (sx, sy)
function isoProject(gx: number, gy: number, h: number = 0): { x: number; y: number } {
  const x = (gx - gy) * (TILE_W / 2);
  const y = (gx + gy) * (TILE_H / 2) - h;
  return { x, y };
}

// ── Farben pro Gebäudetyp ────────────────────────
const PALETTE: Record<string, { top: number; left: number; right: number; roof: number }> = {
  mine:         { top: 0x6b7a8a, left: 0x5a6876, right: 0x4a5661, roof: 0x8a9baa },
  ammo_factory: { top: 0xc0392b, left: 0xa93226, right: 0x8e2419, roof: 0xe74c3c },
  fuel_depot:   { top: 0xd1aa58, left: 0xb89345, right: 0x9e7d33, roof: 0xe8c76a },
  storage:      { top: 0x8e44ad, left: 0x7d3c98, right: 0x6c3483, roof: 0xaf7ac5 },
  tank_factory: { top: 0x2980b9, left: 0x2471a3, right: 0x1f618d, roof: 0x3498db },
  research:     { top: 0x16a085, left: 0x138d75, right: 0x117a65, roof: 0x1abc9c },
  defense:      { top: 0x2c3e50, left: 0x233342, right: 0x1a252f, roof: 0x34495e },
};

// ── Isometrische Pixel-Art Gebäude zeichnen ────
function drawIsoMine(
  g: PIXI.Graphics,
  gx: number, gy: number,
  level: number
) {
  const cx = (gx - gy) * (TILE_W / 2);
  const cy = (gx + gy) * (TILE_H / 2);
  const hw = TILE_W / 2;
  const hh = TILE_H / 2;
  const h = BUILDING_H + level * 4;

  // Base ground
  g.poly([cx, cy, cx + hw, cy - hh, cx, cy - hh * 2, cx - hw, cy - hh])
   .fill({ color: 0x3d3d3d });

  // === Left wall (stone texture) ===
  g.poly([cx - hw, cy - h - hh, cx - hw, cy - hh, cx, cy, cx, cy - h])
   .fill({ color: 0x5a6876 });
  // Stone lines left
  g.poly([cx - hw + 2, cy - h - hh + 6, cx - 2, cy - h + 6])
   .stroke({ color: 0x4a5661, width: 1 });
  g.poly([cx - hw + 4, cy - h - hh + 14, cx - 4, cy - h + 14])
   .stroke({ color: 0x4a5661, width: 1 });
  g.poly([cx - hw + 1, cy - h - hh + 20, cx - 1, cy - h + 20])
   .stroke({ color: 0x4a5661, width: 1 });

  // === Right wall ===
  g.poly([cx, cy - h, cx, cy, cx + hw, cy - hh, cx + hw, cy - h - hh])
   .fill({ color: 0x4a5661 });
  // Stone lines right
  g.poly([cx + 2, cy - h + 6, cx + hw - 2, cy - hh + 6])
   .stroke({ color: 0x3d4a53, width: 1 });

  // === Top face (roof) ===
  g.poly([cx, cy - h, cx + hw, cy - h - hh, cx, cy - h - hh * 2, cx - hw, cy - h - hh])
   .fill({ color: 0x6b7a8a });

  // Roof cross beams
  g.poly([cx - hw + 4, cy - h - hh + 4, cx + hw - 4, cy - h - hh + 4])
   .stroke({ color: 0x8a9baa, width: 1 });
  g.poly([cx - hw + 4, cy - h - hh + 8, cx + hw - 4, cy - h - hh + 8])
   .stroke({ color: 0x8a9baa, width: 1 });

  // === Entrance (mine tunnel) on left face ===
  // Arch
  g.poly([
    cx - hw + 8, cy - hh - 2,
    cx - hw + 8, cy - hh + 4,
    cx - 12, cy + 2,
    cx - 12, cy - h + 8
  ]).fill({ color: 0x1a1a1a });
  // Arch support
  g.poly([
    cx - hw + 7, cy - hh,
    cx - hw + 9, cy - hh,
    cx - 11, cy - h + 8,
    cx - 13, cy - h + 8
  ]).stroke({ color: 0x8b5e3c, width: 1 });

  // === Roof edge highlight ===
  g.poly([cx - hw, cy - h - hh, cx, cy - h, cx + hw, cy - h - hh])
   .stroke({ color: 0xffffff, alpha: 0.15, width: 1 });

  // === Level indicator ===
  const dots = Math.min(level, 3);
  for (let i = 0; i < dots; i++) {
    g.circle(cx - 8 + i * 8, cy - h - hh - 4, 2)
     .fill({ color: 0xffffff, alpha: 0.7 });
  }
}

// ── Iso-Pixel Lagerhalle (weiß, rotes Dach) ─────
function drawIsoStorage(
  g: PIXI.Graphics,
  gx: number, gy: number,
  level: number
) {
  const cx = (gx - gy) * (TILE_W / 2);
  const cy = (gx + gy) * (TILE_H / 2);
  const hw = TILE_W / 2;
  const hh = TILE_H / 2;
  const h = BUILDING_H + level * 3;

  // Ground shadow
  g.poly([cx, cy, cx + hw, cy - hh, cx, cy - hh * 2, cx - hw, cy - hh])
   .fill({ color: 0x2a2a2a });

  // Left wall (white)
  g.poly([cx - hw, cy - h - hh, cx - hw, cy - hh, cx, cy, cx, cy - h])
   .fill({ color: 0xd8d8d8 });
  // Wall panel lines
  g.poly([cx - hw + 2, cy - h - hh + 8, cx - 2, cy - h + 8])
   .stroke({ color: 0xbbbbbb, width: 1 });
  g.poly([cx - hw + 2, cy - h - hh + 16, cx - 2, cy - h + 16])
   .stroke({ color: 0xbbbbbb, width: 1 });

  // Right wall (darker white, shadow side)
  g.poly([cx, cy - h, cx, cy, cx + hw, cy - hh, cx + hw, cy - h - hh])
   .fill({ color: 0xc4c4c4 });
  g.poly([cx + 2, cy - h + 8, cx + hw - 2, cy - hh + 8])
   .stroke({ color: 0xaaaaaa, width: 1 });
  g.poly([cx + 2, cy - h + 16, cx + hw - 2, cy - hh + 16])
   .stroke({ color: 0xaaaaaa, width: 1 });

  // Top face (red flat roof)
  g.poly([cx, cy - h, cx + hw, cy - h - hh, cx, cy - h - hh * 2, cx - hw, cy - h - hh])
   .fill({ color: 0xcc3333 });
  // Roof edge highlight
  g.poly([cx - hw, cy - h - hh, cx, cy - h, cx + hw, cy - h - hh])
   .stroke({ color: 0xffffff, alpha: 0.2, width: 1 });

  // Large window on left face (front)
  // Window frame
  g.poly([cx - hw + 5, cy - hh + 2, cx - hw + 5, cy - hh + 10, cx - 8, cy - h + 6, cx - 8, cy - h - 2])
   .fill({ color: 0x87ceeb, alpha: 0.5 });
  g.poly([cx - hw + 4, cy - hh + 2, cx - hw + 6, cy - hh + 2, cx - 7, cy - h - 2, cx - 9, cy - h - 2])
   .stroke({ color: 0x666666, width: 1 });
  // Window cross
  g.poly([cx - hw + 8, cy - hh + 4, cx - 10, cy - h + 1])
   .stroke({ color: 0x666666, width: 1 });

  // Door on left face (brown, center)
  g.poly([cx - hw + 14, cy - hh + 8, cx - hw + 14, cy - hh + 12, cx - 4, cy - h + 10, cx - 4, cy - h + 6])
   .fill({ color: 0x8b5e3c });
  g.poly([cx - hw + 13, cy - hh + 8, cx - hw + 15, cy - hh + 8, cx - 3, cy - h + 6, cx - 5, cy - h + 6])
   .stroke({ color: 0x6b4226, width: 1 });

  // Small window on right face (side)
  g.poly([cx + 8, cy - h + 6, cx + 8, cy - h + 12, cx + hw - 8, cy - hh + 4, cx + hw - 8, cy - hh - 2])
   .fill({ color: 0x87ceeb, alpha: 0.4 });

  // Roof edge detail (small white border)
  g.poly([cx - hw - 1, cy - h - hh, cx + hw + 1, cy - h - hh])
   .stroke({ color: 0xffffff, alpha: 0.3, width: 1 });

  // Level indicator
  for (let i = 0; i < Math.min(level, 3); i++)
    g.circle(cx - 8 + i * 8, cy - h - hh - 4, 2).fill({ color: 0xffffff, alpha: 0.7 });
}

// ── Generischer Iso-Kubus (für andere Gebäude) ──
function drawIsoCube(
  g: PIXI.Graphics,
  gx: number, gy: number,
  colors: { top: number; left: number; right: number; roof: number },
  h: number = BUILDING_H
) {
  const cx = (gx - gy) * (TILE_W / 2);
  const cy = (gx + gy) * (TILE_H / 2);
  const hw = TILE_W / 2;
  const hh = TILE_H / 2;

  const t: number[] = [cx, cy - h, cx + hw, cy - h - hh, cx, cy - h - hh * 2, cx - hw, cy - h - hh];
  const l: number[] = [cx - hw, cy - h - hh, cx - hw, cy - hh, cx, cy, cx, cy - h];
  const r: number[] = [cx, cy - h, cx, cy, cx + hw, cy - hh, cx + hw, cy - h - hh];

  g.poly(l).fill({ color: colors.left });
  g.poly(r).fill({ color: colors.right });
  g.poly(t).fill({ color: colors.top });
  g.poly(t).stroke({ color: 0xffffff, alpha: 0.15, width: 1 });
}

function drawIsoTile(g: PIXI.Graphics, gx: number, gy: number, color: number, alpha: number = 1) {
  const { x, y } = isoProject(gx, gy);
  g.poly([
    x, y,
    x + TILE_W / 2, y - TILE_H / 2,
    x, y - TILE_H,
    x - TILE_W / 2, y - TILE_H / 2,
  ]).fill({ color, alpha });
}

export default function GameCanvas({ selectedUnit, buildings }: GameCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);

  useEffect(() => {
    if (!containerRef.current || appRef.current) return;

    const app = new PIXI.Application();
    appRef.current = app;

    async function init() {
      try {
        await app.init({
          resizeTo: containerRef.current!,
          background: '#1a2129',
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });
      } catch (e) {
        console.error('PixiJS init failed:', e);
        const el = containerRef.current!;
        el.innerHTML = '<div style="padding:2rem;text-align:center;color:#8895a5">loading...</div>';
        return;
      }

      containerRef.current!.appendChild(app.canvas as HTMLCanvasElement);

      const world = new PIXI.Container();
      app.stage.addChild(world);

      const offsetX = app.screen.width / 2;
      const offsetY = Math.min(app.screen.height / 2, 200);
      world.x = offsetX;
      world.y = offsetY;

      const gridSize = 8;
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          const shade = (gx + gy) % 2 === 0 ? 0x262f38 : 0x1e2730;
          const tile = new PIXI.Graphics();
          drawIsoTile(tile, gx, gy, shade, 1);
          world.addChild(tile);
        }
      }

      // Load tank sprites
      const tankTextures: Record<string, PIXI.Texture> = {};
      const dirs = ['SE', 'SW', 'NW', 'NO'];
      try {
        for (const d of dirs) {
          tankTextures[d] = await PIXI.Assets.load(`/assets/tiger-${d}.png`);
        }
        
        // Place 4 tanks in different directions
        const positions: { dir: string; gx: number; gy: number }[] = [
          { dir: 'SE', gx: 2, gy: 4 },
          { dir: 'SW', gx: 4, gy: 4 },
          { dir: 'NW', gx: 4, gy: 2 },
          { dir: 'NO', gx: 2, gy: 2 },
        ];
        
        for (const p of positions) {
          const sprite = new PIXI.Sprite(tankTextures[p.dir]);
          sprite.anchor.set(0.5, 0.5);
          const pos = isoProject(p.gx, p.gy, 0);
          sprite.x = pos.x;
          sprite.y = pos.y;
          sprite.scale.set(0.8);
          sprite.eventMode = 'static';
          sprite.cursor = 'pointer';
          world.addChild(sprite);
        }
        console.log('[Sprite] 4 Tiger Tanks platziert');
      } catch (e) {
        console.error('[Sprite] Fehler:', e);
      }

      app.ticker.add(() => {});
    }

    init();
    return () => { app.destroy(true); appRef.current = null; };
  }, []);

  // ── Buildings (isometric) ────────────────────
  useEffect(() => {
    const app = appRef.current;
    if (!app) return;

    // Remove old building graphics
    const toRemove = app.stage.children.filter(
      (c) => (c as any)._isBuilding === true
    );
    toRemove.forEach((c) => { app.stage.removeChild(c); c.destroy(true); });

    // Draw new buildings
    buildings.forEach((b) => {
      const g = new PIXI.Graphics();
      (g as any)._isBuilding = true;

      if (b.buildingType === 'mine') {
        drawIsoMine(g, b.gridX, b.gridY, b.level);
      } else if (b.buildingType === 'storage') {
        drawIsoStorage(g, b.gridX, b.gridY, b.level);
      } else {
        // Generic isometric cube for other buildings
        const colors = PALETTE[b.buildingType] || PALETTE.mine;
        const h = b.buildingType === 'tank_factory' ? BUILDING_H * 2 :
                  b.buildingType === 'storage' ? BUILDING_H * 1.5 : BUILDING_H;
        drawIsoCube(g, b.gridX, b.gridY, colors, h);
      }

      // Icon label below
      const label = new PIXI.Text({
        text: b.buildingType === 'mine' ? '⛏' :
              b.buildingType === 'ammo_factory' ? '💥' :
              b.buildingType === 'fuel_depot' ? '⛽' :
              b.buildingType === 'storage' ? '📦' :
              b.buildingType === 'tank_factory' ? '🏭' :
              b.buildingType === 'research' ? '🔬' : '🛡',
        style: { fontSize: 10 }
      });
      const pos = isoProject(b.gridX, b.gridY, -4);
      label.x = pos.x - 6;
      label.y = pos.y;
      label.alpha = 0.6;

      app.stage.addChild(g);
      app.stage.addChild(label);
    });
  }, [buildings]);

  return <div className="game-canvas" ref={containerRef} />;
}
